import React, { useEffect, useState } from 'react';
import { Form, FormFeedback, Input, Label } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Campaign } from '../../types/campaign';
import { updateCampaign } from '../../api/campaignApi';
import { BlockedKeywords } from '../../types/blocked-keywords';
import { getBlockedKeywords } from '../../api/blockedKeywordsApi';

interface EditCampaignModalProps {
    campaign: Campaign;
    isOpen: boolean;
    toggle: () => void;
    onUpdateData: (newData: Campaign) => void;
}

const EditCampaignModal = ({ campaign, isOpen, toggle, onUpdateData }: EditCampaignModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [palavrasChaveProtegida, setPalavrasChaveProtegida] = useState<BlockedKeywords[]>([]);

    useEffect(() => {
        getBlockedKeywords().then(setPalavrasChaveProtegida);
    }, []);

    const toggleModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            id: campaign.id,
            name: campaign.name,
            blockedKeywords: campaign.blockedKeywords.map(p => p.id),
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .trim()
                .required('Nome é obrigatório'),
            blockedKeywords: Yup.array(),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            updateCampaign(values)
                .then((res: any) => {
                    toggleModal();
                    onUpdateData(res);
                    toast.success('Campanha atualizada com sucesso!');
                })
                .catch(() => {
                })
                .finally(() => {
                    setLoading(false);
                });
        },
    });

    return (
        <div className={`modal ${isOpen ? 'show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }} tabIndex={-1}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleForm.handleSubmit();
                        return false;
                    }}>
                        <div className="modal-header bg-light p-3">
                            <h5 className="modal-title">Editar Campanha</h5>
                            <button type="button" className="btn-close" onClick={toggleModal}></button>
                        </div>

                        <div className="modal-body">
                            <div className="mb-3">
                                <Label
                                    htmlFor="name"
                                    className="form-label"
                                >
                                    Nome
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="name"
                                    name="name"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.name}
                                    invalid={
                                        !!(handleForm.touched.name && handleForm.errors.name)
                                    }
                                />
                                {handleForm.touched.name && handleForm.errors.name ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.name}</FormFeedback>
                                ) : null}
                            </div>

                            <div className="mb-3">
                                <Label
                                    htmlFor="palavras-chave-protegidas-field"
                                    className="form-label"
                                >
                                    Proteções: Lista de palavras
                                </Label>
                                <Input
                                    className="form-control"
                                    type="select"
                                    multiple
                                    id="palavras-chave-protegidas-field"
                                    name="blockedKeywords"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.blockedKeywords}
                                    invalid={
                                        !!(handleForm.touched.blockedKeywords && handleForm.errors.blockedKeywords)
                                    }
                                >
                                    {palavrasChaveProtegida.map((palavraChave) => (
                                        <option
                                            key={palavraChave.id}
                                            value={palavraChave.id}
                                        >
                                            {palavraChave.name}
                                        </option>
                                    ))}
                                </Input>
                                {handleForm.touched.blockedKeywords && handleForm.errors.blockedKeywords ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.blockedKeywords}</FormFeedback>
                                ) : null}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="submit"
                                className="btn btn-success btn-label right ms-auto nexttab nexttab"
                            >
                                <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                {loading && <span className="spinner-border spinner-border-sm me-2" role="status"
                                                  aria-hidden="true"></span>}
                                Atualizar
                            </button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default EditCampaignModal;